import isFinite from 'lodash/isFinite';
import range from 'lodash/range';
import clamp from 'lodash/clamp';

export const toFixed = (n, { precision = 0 } = {}) => n.toFixed(precision);

// Private method used for both formatNum and formatPercent.
export const numberWithCommas = (n, options = {}) => {
  const [intStr, fraction] = toFixed(n, options).split('.');
  const len = intStr.length;

  // Count backwards a character at a time. If it's a multiple of 3 from the end
  // and we haven't hit the first digit and it's not the negative sign, stick a comma in there
  const formattedStr = range(1, len + 1).map((i) => {
    const nextDigit = intStr.charAt(len - i);
    return (i % 3 === 0 && i !== len && nextDigit !== '-') ? `,${nextDigit}` : nextDigit;
  }).reverse().join('');

  return fraction ? `${formattedStr}.${fraction}` : formattedStr;
};

export const isRealNumber = n => isFinite(n);

export const realNumberOrZero = n => (isRealNumber(n) ? n : 0);

export const round = (n, { precision = 0, asFormattedString = false } = {}) => {
  // TODO: this is probably rubbish
  /* eslint-disable */
  const x = Math.pow(10, precision);
  /* eslint-enable */
  const rounded = Math.round(x * n) / x;
  if (asFormattedString) {
    const [left, right = ''] = String(rounded).split('.');
    return `${numberWithCommas(Number(left))}${right && `.${right}`}`;
  }
  return rounded;
};

export const percentDifference = (n1, n2, { precision = 0 } = {}) =>
  round(100 * ((n1 - n2) / n2), { precision });

export const isFiniteNotNullNumber = n =>
  typeof n === 'number' &&
  !Number.isNaN(Number(n)) &&
  Number.isFinite(Number(n));

export const prettifyNumber = (n, { precision = 0 } = {}) => {
  if (n >= 1000000000) { return `${round(n / 1000000000, { precision })}B`; }
  if (n >= 1000000) { return `${round(n / 1000000, { precision })}M`; }
  if (n >= 1000) { return `${round(n / 1000, { precision })}k`; }
  return `${round(n, { precision })}`;
};

export const formatNum = (n, { precision = 0 } = {}) => {
  const number = realNumberOrZero(n);
  return numberWithCommas(number, { precision });
};

export const formatPercent = (n, { precision = 0 } = {}) => {
  const number = realNumberOrZero(n) * 100;
  return `${numberWithCommas(number, { precision })}%`;
};

export const formatCurrency = (n, { precision = 2, currency = 'USD' } = {}) => {
  const currencySymbols = { USD: '$' };
  const sign = n < 0 ? '-' : '';

  const number = realNumberOrZero(n) / 100;
  const formattedNumber = numberWithCommas(Math.abs(number), { precision });
  return `${sign}${currencySymbols[currency]}${formattedNumber}`;
};

export const formatDuration = (n, verbose = false) => {
  const units = { s: 'second', m: 'minute', h: 'hour' };
  const getUnit = (u, v) => {
    if (!verbose) { return u; }
    return ` ${units[u]}${v !== 1 ? 's' : ''}`;
  };

  if (!n) { return `0${getUnit('s', 0)}`; }

  let remaining = n;
  const zeroPad = v => (v < 10 ? `0${v}` : v);

  return ['s', 'm', 'h'].reduce((acc, unit) => {
    if (remaining <= 0) { return acc; }

    const value = Math.floor(remaining % 60);
    const s = remaining > 60 && !verbose ? zeroPad(value) : value;
    remaining = Math.floor(remaining / 60);

    return `${s}${getUnit(unit, s)} ${acc}`;
  }, '').trim();
};

export const numDecimals = (n) => {
  const decimalPortion = isFinite(n) && n.toString().split('.')[1];
  return decimalPortion ? decimalPortion.length : 0;
};

// For a given value, returns a suggested number of decimal places.
export const suggestedPrecision = (n) => {
  // If we have no decimals we can't show any
  if (numDecimals(n) === 0) return 0;

  // Not actually complicated.
  // num == 13.2 => Math.log10(num) == 1.12 => precision: 0 => show "13"
  // num == 5.1 => Math.log10(num) == 0.707 => precision: 1 => show "5.1"
  // num == 0.100 => Math.log10(num) == -1 => precision: 2 => show "0.10"
  // num == 0.0099 => Math.log10(num) == -2.004 => precision: 4 => show "0.0099"
  // Uses n && ... so if n is 0 we just return precision 0
  return n && Math.log10(Math.abs(n)) < 1 ? Math.abs(Math.floor(Math.log10(Math.abs(n)))) + 1 : 0;
};

// Attempts to determine what's a good number of decimal places to show.
// Always tries to show atLeast but never shows more than atMost. Does not show
// any if the number has no decimals, but is not currently smart about sig-figs,
// because it would require differentiating between 0.5 and 0.5000 which is non trivial.
export const autoPrecision = (n, { atLeast = 0, atMost = Infinity } = {}) => {
  if (atLeast > atMost) return atMost;
  return clamp(suggestedPrecision(n), atLeast, Math.min(numDecimals(n), atMost));
};

export const autoFormatNum = (n, { atLeast, atMost } = {}) =>
  formatNum(n, { precision: autoPrecision(n, { atLeast, atMost }) });

export const autoFormatPercent = (n, { atLeast = 1, atMost, asPoints = false } = {}) =>
  (asPoints ?
    formatNum(n, { precision: autoPrecision(n * 100, { atLeast, atMost }) }) :
    formatPercent(n, { precision: autoPrecision(n * 100, { atLeast, atMost }) }));

// Never show more than this many digits for any metric
export const GLOBAL_MAX_DIGITS = 4;

export const cappedFormatter =
  (n, { atLeast }) => autoFormatNum(n, { atLeast, atMost: GLOBAL_MAX_DIGITS });

export const cappedPercentFormatter =
  (n, { atLeast }) => autoFormatPercent(n, { atLeast, atMost: GLOBAL_MAX_DIGITS });

export const numberFormatter = cappedFormatter;

export const percentFormatter = (n, { asPoints = false, atLeast } = {}) =>
  (asPoints ?
    cappedFormatter(n * 100, { atLeast }) :
    cappedPercentFormatter(n, { atLeast }));

export const currencyFormatter = (n, options = {}) =>
  (options.currency ?
    formatCurrency(n, options) :
    cappedFormatter(n, options));



// WEBPACK FOOTER //
// ./node_modules/@localytics/loc-components/lib/Utils/number.js